import { FIELDS } from './fields'

/**
 * Odhadne, kolik úvodních řádků je titulek nebo poznámka, ne data.
 *
 * Reálný případ: export začíná řádkem „Přístupové kódy“ a teprve pod ním je
 * skutečná hlavička „Barkód | Sektor“. Bez odříznutí by se za hlavičku
 * považoval titulek.
 *
 * Typický titulek se pozná tak, že má vyplněnou jen první buňku (nebo pár),
 * zatímco zbytek souboru má sloupců výrazně víc. Sázíme na tenhle jediný, ale
 * spolehlivý znak — konzervativně, protože špatný ořez zahodí data.
 */
export function detectSkipRows(rows: string[][]): number {
    if (rows.length < 3) return 0

    // Ořezávat smí jen pár úvodních řádků a nikdy ne většinu souboru.
    const limit = Math.min(5, rows.length - 2)

    // Signál A — řádky nad rozpoznanou hlavičkou.
    //
    // Když někde v úvodu leží řádek se známými názvy sloupců, všechno nad ním
    // je titulek. Funguje i tam, kde signál B selhává: v LUCIE.xlsx mají data
    // vyplněnou jen jednu buňku (sloupec „Sektor“ je prázdný), takže se šířkou
    // od titulku neliší — ale hlavička „Barkód | Sektor“ je rozpoznatelná.
    for (let i = 1; i <= limit; i++) {
        if (looksLikeHeader(rows[i]) && !looksLikeHeader(rows[0])) return i
    }

    // Signál B — řádek výrazně užší než zbytek souboru.
    //
    // Klasický titulek („Export ze systému“) má vyplněnou jen první buňku,
    // zatímco data mají sloupců víc.
    const bodyWidth = medianFilledCount(rows.slice(Math.min(3, rows.length - 1), 30))
    if (bodyWidth < 2) return 0 // data sama o sobě úzká: šířka nic neřekne

    let skip = 0
    while (skip < limit) {
        const filled = filledCount(rows[skip])

        // Řádek s méně než polovinou obvyklých buněk je titulek/poznámka.
        if (filled === 0 || filled * 2 > bodyWidth) break

        // Pojistka: řádek, který sám vypadá jako hlavička, se neořezává —
        // jinak by u souboru s krátkou hlavičkou zmizela ta pravá.
        if (looksLikeHeader(rows[skip])) break

        skip++
    }

    return skip
}

function filledCount(row: string[] | undefined): number {
    return (row ?? []).filter(c => (c ?? '').trim() !== '').length
}

function medianFilledCount(rows: string[][]): number {
    const counts = rows.map(filledCount).filter(n => n > 0).sort((a, b) => a - b)
    if (counts.length === 0) return 0
    return counts[Math.floor(counts.length / 2)]
}

/** Obsahuje řádek známý název sloupce? Pak je to hlavička, ne titulek. */
function looksLikeHeader(row: string[] | undefined): boolean {
    const known = new Set(FIELDS.flatMap(f => f.aliases.map(normalise)))
    return (row ?? []).some(c => (c ?? '').trim() !== '' && known.has(normalise(c)))
}

function normalise(value: string): string {
    return value
        .trim()
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9]/g, '')
}
